import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import toast from "react-hot-toast";
import { signIn, generateOtp } from "../../services/authServices";
import { getDeviceInfo } from "../../utils/deviceInfo";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogId } from "../../actions/actions";
import logger from "../../utils/logger";

interface MpinLoginProps {
  onBackToPhone: () => void;
  onMpinNotSet: (phone: string, countryCode: string) => void;
}

const MpinLogin: React.FC<MpinLoginProps> = ({ onBackToPhone, onMpinNotSet }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("91");
  const [mpin, setMpin] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPin, setShowPin] = useState<boolean>(false);
  const [attemptCount, setAttemptCount] = useState<number>(0);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [blockTime, setBlockTime] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Clear PIN setup flag when component mounts (user navigated away)
  useEffect(() => {
    return () => {
      // Only clear flag if we're not in the middle of a PIN setup flow
      const mpinSetupType = localStorage.getItem("mpinSetupType");
      if (localStorage.getItem("showMpinSetup") && !mpinSetupType) {
        localStorage.removeItem("showMpinSetup");
      }
      // Clear sensitive data
      setMpin("");
      setShowPin(false);
    };
  }, []);

  // Clear PIN input when component unmounts or on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setShowPin(false); // Hide PIN when tab becomes inactive
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const validatePhoneNumber = (number: string): boolean => {
    const phoneNumberPattern = /^[1-9]\d{9}$/;
    return phoneNumberPattern.test(number);
  };

  const handlePhoneChange = (value: string, country: any) => {
    const newCountryCode = country.dialCode;
    const numberWithoutCode = value.slice(newCountryCode.length);

    setPhoneNumber(numberWithoutCode);
    setCountryCode(newCountryCode);
    setIsValid(validatePhoneNumber(numberWithoutCode));
  };

  const handleMpinChange = (value: string) => {
    // Only allow numeric input and limit to 4 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    setMpin(numericValue);
  };

  // Rate limiting function - VERY GENEROUS FOR TESTING
  const checkRateLimit = () => {
    const key = `mpin_attempts_${phoneNumber}`;
    const attempts = JSON.parse(localStorage.getItem(key) || '[]');
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes instead of 1 hour
    
    // Filter attempts from last 5 minutes
    const recentAttempts = attempts.filter((time: number) => now - time < fiveMinutes);
    
    // Allow 20 attempts in 5 minutes (very generous)
    if (recentAttempts.length >= 20) {
      setIsBlocked(true);
      const oldestAttempt = Math.min(...recentAttempts);
      setBlockTime(Math.ceil((fiveMinutes - (now - oldestAttempt)) / 60000)); // minutes
      return false;
    }
    
    return true;
  };

  const recordAttempt = () => {
    const key = `mpin_attempts_${phoneNumber}`;
    const attempts = JSON.parse(localStorage.getItem(key) || '[]');
    attempts.push(Date.now());
    localStorage.setItem(key, JSON.stringify(attempts));
    setAttemptCount(attempts.length);
  };

  // Helper function to clear rate limit (for testing)
  const clearRateLimit = () => {
    const key = `mpin_attempts_${phoneNumber}`;
    localStorage.removeItem(key);
    setAttemptCount(0);
    setIsBlocked(false);
    setBlockTime(0);
    toast.success("Rate limit cleared!");
  };

  const handlePinSetupFlow = async (isReset = false) => {
    // Validate phone number first
    if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
      toast.error("Please enter a valid phone number first");
      return;
    }

    logger.log("[MPIN Login] Initiating PIN setup flow:", {
      setupType: isReset ? 'reset' : 'setup',
      phone: phoneNumber,
      countryCode: countryCode
    });

    try {
      // Generate OTP for the phone number
      const response = await generateOtp({
        phone: phoneNumber,
        countryCode: countryCode,
        otpType: "custom_sms_otp",
      });

      if (response && response.status) {
        // Store phone details and dispatch logId
        localStorage.setItem("phone", phoneNumber);
        localStorage.setItem("countryCode", countryCode);
        localStorage.setItem("showMpinSetup", "true");
        localStorage.setItem("mpinSetupType", isReset ? "reset" : "setup");
        dispatch(setLogId(response?.data?.data?.LogID));

        logger.log("[MPIN Login] Setup flags stored, navigating to OTP verification");
        toast.success(`OTP sent! Please verify to ${isReset ? 'reset' : 'set up'} your PIN.`);
        navigate("/otp-verify");
      }
    } catch (error) {
      logger.error("[MPIN Login] Error generating OTP:", error);
      toast.error("Failed to send OTP. Please try again.");
      // Fallback to manual phone entry
      onMpinNotSet(phoneNumber, countryCode);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhoneNumber(phoneNumber)) {
      setIsValid(false);
      return;
    }

    if (mpin.length !== 4) {
      toast.error("Please enter your 4-digit PIN");
      return;
    }

    // Check rate limiting
    if (!checkRateLimit()) {
      toast.error(`Too many attempts. Please wait ${blockTime} minutes before trying again.`);
      return;
    }

    if (isBlocked) {
      toast.error(`Account temporarily blocked. Please wait ${blockTime} minutes.`);
      return;
    }

    setIsLoading(true);
    recordAttempt();

    try {
      const data = {
        loginType: "mpin",
        loggedFrom: "web",
        deviceToken: JSON.parse(localStorage.getItem('fcm_token_data') || '{}').token,
        countryCode: countryCode,
        phone: phoneNumber,
        mpin: mpin,
        deviceInfo: await getDeviceInfo(),
      };

      const resp = await signIn(data);
      
      if (resp && resp.status) {
        localStorage.setItem("auth_token", resp?.data?.data?.token);
        localStorage.setItem("userData", JSON.stringify(resp?.data?.data));

        // 🏢 STORE BUSINESS DATA - same as OTP login
        if (resp?.data?.data?.multiAccountList?.length) {
          logger.log('🏢 [MPIN] Storing userBusinesses - Count:', resp.data.data.multiAccountList.length);
          logger.log('🏢 [MPIN] Business list:', resp.data.data.multiAccountList);
          localStorage.setItem(
            "userBusinesses",
            JSON.stringify(resp.data.data.multiAccountList)
          );
          logger.log('🏢 [MPIN] Stored in localStorage - userBusinesses set');
        } else {
          logger.warn('🏢 [MPIN] No multiAccountList found or empty array');
        }

        // Store user preferences if available
        if (resp.data.data.userPreference) {
          localStorage.setItem(
            "user_preferences",
            JSON.stringify(resp.data.data.userPreference)
          );
        }

        logger.log("🔑 MPIN LOGIN - Data stored:", {
          timestamp: new Date().toISOString(),
          loginType: 'mpin',
          userData: resp?.data?.data,
          hasUserPreference: !!resp?.data?.data?.userPreference,
          businessCount: resp?.data?.data?.multiAccountList?.length || 0,
          userDetailsType: Array.isArray(resp?.data?.data) ? 'ARRAY' : 'OBJECT'
        });

        if (resp?.data?.data?.isOnboardCompleted) {
          if (resp?.data?.data?.isSubscriptionActive) {
            toast.success("Logged In Successfully!");
            navigate("/leads", { replace: true });
          } else {
            navigate("/SubscriptionExpiredPage", { replace: true });
          }
        } else {
          navigate("./sign-up");
        }
      }
    } catch (error: any) {
      logger.error("MPIN Login Error:", error);
      
      // Extract specific error message from API response
      const errorMessage = error.response?.data?.message || "Login failed. Please try again.";
      const statusCode = error.response?.status;
      
      logger.log("🔴 MPIN Login Error Details:", {
        statusCode,
        errorMessage,
        fullResponse: error.response?.data
      });
      
      // Check if error indicates MPIN not set or invalid MPIN
      if (statusCode === 422) {
        // API returned 422 - check if it's "not set" or "invalid"
        const isNotSet = errorMessage.toLowerCase().includes('not set') || 
                        errorMessage.toLowerCase().includes('not found') ||
                        errorMessage.toLowerCase().includes('no mpin') ||
                        errorMessage.toLowerCase().includes('mpin not');
        
        if (isNotSet) {
          // MPIN not set - redirect to setup flow
          toast.error("PIN not set. Sending OTP to set it up...");
          await handlePinSetupFlow(false);
        } else {
          // Invalid MPIN - show error
          toast.error(`${errorMessage}`);
        }
      } else if (statusCode === 400 || statusCode === 404) {
        // User hasn't set MPIN yet, generate OTP and redirect
        toast.error("PIN not set. Sending OTP to set it up...");
        await handlePinSetupFlow(false);
      } else if (statusCode === 401) {
        toast.error(errorMessage || "Invalid MPIN. Please try again.");
      } else if (statusCode === 403 && 
                 error.response.data?.data?.isFreePlan) {
        navigate("/SubscriptionExpiredPage", { replace: true });
      } else {
        // Show specific API error message or fallback
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center login_section">
        <div className="col-12 col-md-6 col-lg-5 col-xl-4 login_card">
          <div
            className="card login-card shadow-lg p-4"
            style={{
              maxWidth: "650px",
              width: "100%",
              margin: "0 auto",
              borderRadius: "15px",
              backgroundColor: "#ebeff4",
            }}
          >
            <div className="d-flex flex-column align-items-center" style={{ width: "100%" }}>
              <img
                src="assets/img/3sigma_logo.png"
                alt="logo"
                className="mb-4"
                style={{
                  width: "100%",
                  maxWidth: "200px",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
              <div className="login-header text-center mb-4">
                <h2 style={{ fontWeight: "bold", color: "#333", fontSize: "1.75rem" }}>
                  Quick Login
                </h2>
                <p className="text-muted mt-2" style={{ fontSize: "0.95rem" }}>
                  Enter your phone number and 4-digit PIN
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="login_form w-100">
                <div className="form_container_login">
                  <PhoneInput
                    country="in"
                    value={`${countryCode}${phoneNumber}`}
                    onChange={handlePhoneChange}
                    inputClass={`phone_input_left ${!isValid ? "is-invalid" : ""}`}
                    containerStyle={{ marginBottom: "1rem", width: "100%" }}
                    inputStyle={{
                      width: "100%",
                      height: "50px",
                      fontSize: "16px",
                    }}
                    buttonStyle={{
                      backgroundColor: "#FFF",
                      borderRightWidth: 0,
                      borderTopLeftRadius: 10,
                      borderBottomLeftRadius: 10,
                    }}
                  />
                  {!isValid && (
                    <small className="text-danger mb-3 ps-0">
                      Please enter a valid mobile number
                    </small>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: "600", color: "#333" }}>
                      4-digit PIN
                    </label>
                    <div className="position-relative">
                      <input
                        type={showPin ? "text" : "password"}
                        className="form-control"
                        value={mpin}
                        onChange={(e) => handleMpinChange(e.target.value)}
                        placeholder="••••"
                        maxLength={4}
                        autoComplete="off"
                        spellCheck="false"
                        autoCapitalize="off"
                        autoCorrect="off"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        style={{
                          height: "50px",
                          fontSize: "20px",
                          textAlign: "center",
                          letterSpacing: "12px",
                          borderRadius: "8px",
                          border: isBlocked ? "2px solid #dc3545" : "2px solid #ddd",
                          fontWeight: "bold",
                          paddingRight: "45px",
                        }}
                        disabled={isBlocked || isLoading}
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute"
                        onClick={() => setShowPin(!showPin)}
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          padding: "0",
                          border: "none",
                          background: "none",
                          fontSize: "18px",
                          color: "#6c757d",
                        }}
                      >
                        {showPin ? "🙈" : "👁️"}
                      </button>
                    </div>
                    
                    {isBlocked && (
                      <div className="text-center mt-2">
                        <small className="text-danger d-block">
                          🔒 Too many failed attempts. Please wait {blockTime} minutes.
                        </small>
                        <button
                          type="button"
                          className="btn btn-link btn-sm mt-1"
                          style={{ fontSize: "0.75rem", textDecoration: "underline" }}
                          onClick={clearRateLimit}
                        >
                          🔧 Clear Rate Limit (Testing)
                        </button>
                      </div>
                    )}
                    
                  </div>
                  
                  <button
                    type="submit"
                    className="btn btn-primary w-100 mt-3"
                    style={{
                      backgroundColor: "#3A4B86",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      borderRadius: "8px",
                    }}
                    disabled={isLoading || !isValid || mpin.length !== 4 || isBlocked}
                  >
                    {isLoading ? "Logging In..." : isBlocked ? "Account Blocked" : "Login"}
                  </button>
                  
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-link"
                      style={{ fontSize: "0.95rem", textDecoration: "none" }}
                      onClick={onBackToPhone}
                    >
                      ← Back to Phone Login
                    </button>
                  </div>
                  
                  <div className="text-center mt-2">
                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        style={{ fontSize: "0.85rem", textDecoration: "underline" }}
                        onClick={() => handlePinSetupFlow(false)}
                      >
                        Setup PIN
                      </button>
                      <span className="text-muted" style={{ fontSize: "0.85rem" }}>•</span>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        style={{ fontSize: "0.85rem", textDecoration: "underline" }}
                        onClick={() => handlePinSetupFlow(true)}
                      >
                        Reset PIN
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MpinLogin;